import { ProdutoMarkup } from 'app/types/Markup';
import globalMU from 'globalMU';
import debounce from "lodash/debounce";
import indexOf from "lodash/indexOf";
import sortBy from "lodash/sortBy";
import * as R from 'ramda';
import React, { useEffect, useRef, useMemo, useCallback, useContext } from 'react';
import { EstilosIndividuaisNomeColunasMarkup } from './EstilosIndividuaisNomeColunasMarkup';
import { AcoesHeadMarkup } from './table-head/AcoesHeadMarkup';
import { HeadGridMarkup } from './table-head/HeadGridMarkup';
import clsx from 'clsx';
import { TelaMarkupContext } from '../TelaMarkup';

const estilosTabelas = require('../../../estilos/tabelas.module.scss').default
const estilosBotoes = require('../../../estilos/botoes.module.scss').default
import { fonteGradeEmPxOuPadrao } from 'app/utils/tamanhoFonteGrid'

const tryParseBrazilianNumberSilent = (value: string): string | null => {
    const normalized = value.trim().replace(/\./g, '').replace(',', '.');
    if (normalized === '') return null;
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? null : parsed.toString();
};

export const GridMarkup = () => {
    const props = useContext(TelaMarkupContext);
    const isMounted = useRef(true);
    const [precoAtual, setPrecoAtual] = React.useState<string[]>([])
    const [touchPrecoAtual, setTouchPrecoAtual] = React.useState<boolean>(false)
    const [margemLucro, setMargemLucro] = React.useState<string[]>([])
    const [touchMargemLucro, setTouchMargemLucro] = React.useState<boolean>(false)
    const [produtosMarkup, setProdutosMarkup] = React.useState<ProdutoMarkup[]>(props.markup.Produtos)

    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (isMounted.current) {
            setPrecoAtual(props.markup.Produtos.map(p => globalMU.formatarValorNumerico((p.PrecoAtual ?? 0).toString(), props.mascaraPrecoVenda, false)))
            setMargemLucro(props.markup.Produtos.map(p => globalMU.formatarValorNumerico((p.MargemLucro ?? 0).toString(), 2, false)))
        }
    }, [props.mascaraPrecoVenda, props.markup.Produtos.length])

    useEffect(() => {
        if (isMounted.current) {
            setProdutosMarkup(props.markup.Produtos)
            if (!touchPrecoAtual) {
                setPrecoAtual(props.markup.Produtos.map(p => globalMU.formatarValorNumerico((p.PrecoAtual ?? 0).toString(), props.mascaraPrecoVenda, false)))
            }
            if (!touchMargemLucro) {
                setMargemLucro(props.markup.Produtos.map(p => globalMU.formatarValorNumerico((p.MargemLucro ?? 0).toString(), 2, false)))
            }
        }
    }, [props.markupAtualizado])

    const debouncedAlterarPrecoAtual = useMemo(
        () => debounce((index: number, value: string) => {
            if (isMounted.current) {
                props.onAlterarPrecoAtual(index, value);
            }
        }, 300),
        [props.onAlterarPrecoAtual]
    )

    const debouncedAlterarMargemLucro = useMemo(
        () => debounce((index: number, value: string) => {
            if (isMounted.current) {
                props.onAlterarMargemLucro(index, value);
            }
        }, 300),
        [props.onAlterarMargemLucro]
    )

    useEffect(() => {
        return () => {
            debouncedAlterarPrecoAtual.cancel();
            debouncedAlterarMargemLucro.cancel();
        }
    }, [debouncedAlterarPrecoAtual, debouncedAlterarMargemLucro])

    const handlePrecoAtualChange = useCallback((index: number, value: string) => {
        if (isMounted.current) {
            setPrecoAtual(prev => [...prev.slice(0, index), value, ...prev.slice(index + 1)])
            setTouchPrecoAtual(true)
            const parsed = tryParseBrazilianNumberSilent(value);
            if (parsed != null) {
                debouncedAlterarPrecoAtual(index, parsed);
            }
        }
    }, [debouncedAlterarPrecoAtual])

    const handlePrecoAtualBlur = useCallback((index: number, value: string) => {
        if (!touchPrecoAtual || !isMounted.current) return;
        const parsed = tryParseBrazilianNumberSilent(value);
        if (parsed != null) {
            debouncedAlterarPrecoAtual.cancel();
            props.onAlterarPrecoAtual(index, parsed);
            setPrecoAtual(prev => [...prev.slice(0, index), globalMU.formatarValorNumerico(parsed, props.mascaraPrecoVenda, false), ...prev.slice(index + 1)])
        } else {
            const valorPersistido = props.markup.Produtos[index]?.PrecoAtual;
            if (valorPersistido != null) {
                setPrecoAtual(prev => [...prev.slice(0, index), globalMU.formatarValorNumerico(valorPersistido.toString(), props.mascaraPrecoVenda, false), ...prev.slice(index + 1)])
            }
        }
        setTouchPrecoAtual(false)
    }, [touchPrecoAtual, props.onAlterarPrecoAtual, props.mascaraPrecoVenda, debouncedAlterarPrecoAtual, props.markup.Produtos])

    const handleMargemLucroChange = useCallback((index: number, value: string) => {
        if (isMounted.current) {
            setMargemLucro(prev => [...prev.slice(0, index), value, ...prev.slice(index + 1)])
            setTouchMargemLucro(true)
            const parsed = tryParseBrazilianNumberSilent(value);
            if (parsed != null) {
                debouncedAlterarMargemLucro(index, parsed);
            }
        }
    }, [debouncedAlterarMargemLucro])

    const handleMargemLucroBlur = useCallback((index: number, value: string) => {
        if (!touchMargemLucro || !isMounted.current) return;
        const parsed = tryParseBrazilianNumberSilent(value);
        if (parsed != null) {
            debouncedAlterarMargemLucro.cancel();
            props.onAlterarMargemLucro(index, parsed);
            setMargemLucro(prev => [...prev.slice(0, index), globalMU.formatarValorNumerico(parsed, 2, false), ...prev.slice(index + 1)])
        } else {
            const valorPersistido = props.markup.Produtos[index]?.MargemLucro;
            if (valorPersistido != null) {
                setMargemLucro(prev => [...prev.slice(0, index), globalMU.formatarValorNumerico(valorPersistido.toString(), 2, false), ...prev.slice(index + 1)])
            }
        }
        setTouchMargemLucro(false)
    }, [touchMargemLucro, props.onAlterarMargemLucro, debouncedAlterarMargemLucro, props.markup.Produtos])

    const itensMarkup = useCallback(({ index, nomeColuna, item }: { index: number, nomeColuna: string, item: ProdutoMarkup }) => {

        const fn = R.cond([
            [R.equals("Codigo"), _ => item.Codigo],
            [R.equals("Descricao"), _ => item.Descricao],
            [R.equals("CodigoFabricante"), _ => item.CodigoFabricante],
            [R.equals("Unidade"), _ => item.Unidade],
            [R.equals("MarkupMinimoPapelaria"), _ => globalMU.formatarValorNumerico(item.MarkupMinimo, 2, false)],
            [R.equals("ValorMargemZeroPapelaria"), _ => globalMU.formatarValorNumerico(item.CustoUltimaPesquisa, 3, false)],
            [R.equals("PrecoAtualPapelaria"), _ => {
                /* 435964
                if (props.substituirMarkupPorMargem && props.permiteInteragirMargemLucro) {
                */
                if (!props.somenteLeitura && props.permiteInteragirMargemLucro) //435964
                {
                    return <input type="text"
                        value={(precoAtual[index]) ?? ""}
                        id={`PrecoAtualPapelaria_${index}`}
                        disabled={props.processando || props.somenteLeitura} // 435964
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => handlePrecoAtualChange(index, e.target.value)}
                        onBlur={(e) => handlePrecoAtualBlur(index, e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.code === 'NumpadEnter') {
                                handlePrecoAtualBlur(index, e.currentTarget.value);
                                e.preventDefault(); // Previne comportamento padrão do Enter
                            }
                        }}
                        style={{ width: "80px", textAlign: "right", maxWidth: "100%" }}
                        inputMode='numeric'
                        pattern='[0-9]*'
                    />
                }
                return globalMU.formatarValorNumerico(item.PrecoAtual, props.mascaraPrecoVenda, true)
            }],
            [R.equals("MargemLucro"), _ => {
                /* 435964
                if (props.substituirMarkupPorMargem && props.permiteInteragirMargemLucro) {
                */
                if (!props.somenteLeitura && props.permiteInteragirMargemLucro) //435964
                {
                    return <input type="text"
                        value={(margemLucro[index]) ?? ""}
                        id={`MargemLucro_${index}`}
                        disabled={props.processando || props.somenteLeitura} // 435964
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => handleMargemLucroChange(index, e.target.value)}
                        onBlur={(e) => handleMargemLucroBlur(index, e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.code === 'NumpadEnter') {
                                handleMargemLucroBlur(index, e.currentTarget.value);
                                e.preventDefault(); // Previne comportamento padrão do Enter
                            }
                        }}
                        style={{ width: "80px", textAlign: "right", maxWidth: "100%" }}
                        inputMode='numeric'
                        pattern='[0-9]*'
                    />
                }
                return globalMU.formatarValorNumerico(item.MargemLucro, 2, false) // 449132 item.MargemLucro.toString()
            }],
            [R.equals("MarkupRealPapelaria"), _ => globalMU.formatarValorNumerico(item.MarkupUltimaCompra, 2, false)],
            [R.equals("CustoRealPapelaria"), _ => globalMU.formatarValorNumerico(item.CustoUltimaCompra, props.mascaraPrecoCompra, false)],
            [R.equals("MarkupReal"), _ => globalMU.formatarValorNumerico(item.MarkupReal, 2, false)],
            [R.equals("MarkupRealSimples"), _ => globalMU.formatarValorNumerico(item.MarkupRealSimples, 3, false)], // 452507
            [R.equals("CustoReal1"), _ => globalMU.formatarValorNumerico(item.CustoReal, props.mascaraPrecoCompra, false)],
            [R.equals("CustoAnterior"), _ => globalMU.formatarValorNumerico(item.CustoAnterior, props.mascaraPrecoCompra, true)],
            [R.equals("CustoAtual"), _ => globalMU.formatarValorNumerico(item.CustoAtual, props.mascaraPrecoCompra, true)],
            [R.equals("CustoReal"), _ => globalMU.formatarValorNumerico(item.CustoReal, props.mascaraPrecoCompra, true)],
            [R.equals("CustoUltimaPesquisa"), _ => globalMU.formatarValorNumerico(item.CustoUltimaPesquisa, props.mascaraPrecoCompra, true)],
            [R.equals("MarkupUltimaPesquisa"), _ => globalMU.formatarValorNumerico(item.MarkupUltimaPesquisa, 2, false)],
            [R.equals("MarkupPrecoVendaUltimaPesquisa"), _ => globalMU.formatarValorNumerico(item.MarkupPrecoVendaUltimaPesquisa, 2, false)],
            [R.equals("MargemZeroUltPesquisa"), _ => globalMU.formatarValorNumerico(item.MargemZeroUltPesquisa, 2, false)], // 449132
            [R.equals("MargemLucroAnterior"), _ => globalMU.formatarValorNumerico(item.MargemLucroAnterior, 2, false)],
            [R.equals("MargemLucroReal"), _ => globalMU.formatarValorNumerico(item.MargemLucroReal, 2, false)],
            [R.equals("MargemLucroZeroPorMl"), _ => globalMU.formatarValorNumerico(item.MargemLucroZeroPorMl, 2, false)],
            [R.equals("MarkupAnterior"), _ => globalMU.formatarValorNumerico(item.MarkupAnterior, 2, false)],
            [R.equals("MarkupMargemAtual"), _ => globalMU.formatarValorNumerico(item.MarkupMargemAtual, 2, false)],
            [R.equals("MarkupBruto"), _ => globalMU.formatarValorNumerico(item.MarkupBruto, 2, false)],
            [R.equals("MarkupBrutoValorPresente"), _ => globalMU.formatarValorNumerico(item.MarkupBrutoValorPresente, 2, false)],
            [R.equals("MarkupMargem"), _ => globalMU.formatarValorNumerico(item.MarkupMargem, 2, false)],
            [R.equals("MarkupMargemPorMargem"), _ => globalMU.formatarValorNumerico(item.MarkupMargemPorMargem, 2, false)],
            [R.equals("MarkupMargemValorPresente"), _ => globalMU.formatarValorNumerico(item.MarkupMargemValorPresente, 2, false)],
            [R.equals("MarkupRealValorPresente"), _ => globalMU.formatarValorNumerico(item.MarkupRealValorPresente, 2, false)],
            [R.equals("MarkupRealPorMargem"), _ => globalMU.formatarValorNumerico(item.MarkupRealPorMargem, 2, false)],
            [R.equals("PrecoAnterior"), _ => globalMU.formatarValorNumerico(item.PrecoAnterior, props.mascaraPrecoVenda, true)],
            [R.equals("PrecoAtualValorPresente"), _ => globalMU.formatarValorNumerico(item.PrecoAtualValorPresente, props.mascaraPrecoVenda, true)],
            [R.equals("PrecoCompra"), _ => globalMU.formatarValorNumerico(item.PrecoCompra, props.mascaraPrecoCompra, true)],
            [R.equals("PrecoCustoMl"), _ => globalMU.formatarValorNumerico(item.PrecoCustoMl, props.mascaraPrecoCompra, true)],
            [R.equals("PrecoLiquidoMl"), _ => globalMU.formatarValorNumerico(item.PrecoLiquidoMl, props.mascaraPrecoVenda, true)],
            [R.equals("PrecoVendaMl"), _ => globalMU.formatarValorNumerico(item.PrecoVendaMl, props.mascaraPrecoVenda, true)],
            [R.equals("Quantidade"), _ => globalMU.formatarValorNumerico(item.Quantidade, props.mascaraQuantidade, false)],
        ])

        return <td
            className="_table-cell"
            id={`GridMarkup_Celula_${nomeColuna.replace(/\s+/g, '_')}_${index}`}
            key={`${index}_${nomeColuna.replace(/\s+/g, '_')}_${item.CodigoItem}`}
            style={EstilosIndividuaisNomeColunasMarkup(nomeColuna)}
        >
            {fn(nomeColuna)}
        </td>
    }, [precoAtual, margemLucro, props.permiteInteragirMargemLucro, props.mascaraPrecoVenda, props.mascaraPrecoCompra, props.mascaraQuantidade, handlePrecoAtualChange, handlePrecoAtualBlur, handleMargemLucroChange, handleMargemLucroBlur, props.processando])

    const ordenacao = useMemo(() => [
        "Codigo",
        "Descricao",
        "CodigoFabricante",
        "Unidade",
        "MarkupMinimoPapelaria",
        "ValorMargemZeroPapelaria",
        "PrecoAtualPapelaria",
        "MargemLucro",
        "MarkupRealPapelaria",
        "CustoRealPapelaria",
        "MarkupReal",
        "MarkupRealSimples", // 452507
        "CustoReal1",
        "CustoAnterior",
        "CustoAtual",
        "CustoReal",
        "CustoUltimaPesquisa",
        "MarkupUltimaPesquisa",
        "MarkupPrecoVendaUltimaPesquisa",
        "MargemZeroUltPesquisa", // 449132
        "MargemLucroAnterior",
        "MargemLucroReal",
        "MargemLucroZeroPorMl",
        "MarkupAnterior",
        "MarkupMargemAtual",
        "MarkupBruto",
        "MarkupBrutoValorPresente",
        "MarkupMargem",
        "MarkupMargemPorMargem",
        "MarkupMargemValorPresente",
        "MarkupRealValorPresente",
        "MarkupRealPorMargem",
        "PrecoAnterior",
        "PrecoAtualValorPresente",
        "PrecoCompra",
        "PrecoCustoMl",
        "PrecoLiquidoMl",
        "PrecoVendaMl",
        "Quantidade"
    ], []);

    const colunasFiltradas = useMemo(() =>
        (props.colunas ?? []).filter(coluna => ordenacao.includes(coluna.Nome) && coluna.Permitido)
        , [props.colunas, ordenacao])

    const colunasOrdenadas = useMemo(() =>
        sortBy(colunasFiltradas, (coluna) => indexOf(ordenacao, coluna.Nome))
        , [colunasFiltradas, ordenacao])

    const colunasSelecionadas = useMemo(() =>
        colunasOrdenadas.filter(c => c.Selecionada && c.Permitido)
        , [colunasOrdenadas])

    const renderLinha = useCallback((item: ProdutoMarkup, index: number) => (
        <tr className="_table-row"
            id={`GridMarkup_Linha_${index}_${item.CodigoItem}_${item.Codigo}`}
            key={`${item.CodigoItem}_${item.Codigo}_${index}`}
        >
            {colunasSelecionadas.map((coluna) => (
                itensMarkup({
                    index: index,
                    nomeColuna: coluna.Nome,
                    item: item
                }))
            )}
        </tr>
    ), [colunasSelecionadas, itensMarkup])

    const botaoAplicarAlteracoes = useMemo(() => {
        /* 435964
        props.substituirMarkupPorMargem && props.permiteInteragirMargemLucro &&
        */
        if (!props.somenteLeitura && props.permiteInteragirMargemLucro) { // 435964
            const desabilitado = props.processando || props.somenteLeitura || !props.dadosAlterados ||
                props.simulacaoEmAndamento || touchPrecoAtual || touchMargemLucro;
            return (
                <button className={clsx(estilosBotoes.botaoPrimario, '_mt-3')} onClick={props.onAplicarPrecos}
                    disabled={desabilitado}
                    id="GridMarkup_BotaoAplicarAlteracoes"
                >
                    {props.processando ? "Processando..." : "Aplicar Alterações"}
                </button>
            )
        }
        return null;
    }, [props.permiteInteragirMargemLucro, props.onAplicarPrecos, props.processando, props.somenteLeitura, props.dadosAlterados, props.simulacaoEmAndamento, touchPrecoAtual, touchMargemLucro])

    return <>
        <AcoesHeadMarkup
            colunas={colunasOrdenadas}
        />

        <div style={{ overflow: "auto", fontSize: fonteGradeEmPxOuPadrao(props.tamanhoFontePadrao), marginRight: "10px" }}
            id="GridMarkup_Tabela_DivRolavel"
        >
            <table className={`_table _w-full _border-collapse ${estilosTabelas.tabela}`}
                id="GridMarkup_Tabela"
            >
                <HeadGridMarkup colunas={colunasOrdenadas} />

                <tbody className="_table-row-group" id="GridMarkup_CorpoTabela">
                    {precoAtual.length > 0 && produtosMarkup.map((item, index) =>
                        renderLinha(item, index)
                    )}
                </tbody>
            </table>
        </div>

        {botaoAplicarAlteracoes}
    </>
}